import { Flex, FloraButton } from '@grupoboticario/flora-react';
import { TrayArrowDownIcon } from '@grupoboticario/flora-react-icons';
import { useParams } from 'react-router-dom';

import { Skeleton, Tag, Card, ResponsiveFloraTypography } from 'components';
import config from 'config';
import { ORDERS_STATUS } from 'config/environments/shared';
import { getOrderStatus, getFormattedPrice } from 'helpers';
import { useOrderDetail } from 'hooks';

import Styles from './Summary.styles';

export const Summary = () => {
  const { id = '0' } = useParams<{ id: string }>();
  const { data: order, isLoading } = useOrderDetail(id);
  const tagStatus = order && getOrderStatus({ status: order.status, dateTime: order.orderDate });

  if (isLoading) {
    return (
      <Styles.Wrapper data-testid='skeleton-summary'>
        <Card>
          <Skeleton height='$10' width='100%' />
          <Styles.Divider variant='Line' />
          <Flex direction='column' gap='$4'>
            <Skeleton height='$5' width='100%' />
            <Skeleton height='$5' width='100%' />
            <Skeleton height='$5' width='100%' />
            <Skeleton height='$15' width='100%' />
          </Flex>
        </Card>
      </Styles.Wrapper>
    );
  }

  return (
    <Styles.Wrapper>
      <Card>
        <ResponsiveFloraTypography as='h2' fontSize='subtitle' fontWeight='bold'>
          Informações do pedido
        </ResponsiveFloraTypography>
        <Styles.Divider variant='Line' />
        <Flex direction='column' gap='$4'>
          <Flex justify='space-between'>
            <ResponsiveFloraTypography aria-hidden>Status do pedido</ResponsiveFloraTypography>
            {tagStatus && <Tag data={tagStatus} type='pedido' />}
          </Flex>

          <Flex justify='space-between'>
            <ResponsiveFloraTypography>Valor total</ResponsiveFloraTypography>
            <ResponsiveFloraTypography>
              {order && getFormattedPrice(order.amount)}
            </ResponsiveFloraTypography>
          </Flex>
        </Flex>
      </Card>
      {order?.status !== ORDERS_STATUS.CANCELED && (
        <Card>
          <ResponsiveFloraTypography as='h2' fontSize='subtitle' fontWeight='bold'>
            Detalhes
          </ResponsiveFloraTypography>
          <Styles.Divider variant='Line' />
          <Flex direction='column' gap='$4'>
            <Flex justify='space-between'>
              <ResponsiveFloraTypography>Lucro previsto</ResponsiveFloraTypography>
              <ResponsiveFloraTypography>
                {order && getFormattedPrice(order.profit)}
              </ResponsiveFloraTypography>
            </Flex>

            <Flex justify='space-between'>
              <ResponsiveFloraTypography>Percentual de lucro</ResponsiveFloraTypography>
              <ResponsiveFloraTypography>
                {order && `${order.profitPercentage}%`}
              </ResponsiveFloraTypography>
            </Flex>

            {config.app.short === 'bot' && order?.orderSummaryFileLink && (
              <FloraButton
                as='a'
                download={`Extrato Completo - ${order.id}.pdf`}
                href={order.orderSummaryFileLink}
                target='_blank'
                rel='noopener noreferrer'
                hierarchy='primary'
                autotagging-flora='on'
                data-flora='button'
                data-flora-text='baixar-extrato-completo'
                has='iconRight'
                icon={<TrayArrowDownIcon />}
              >
                <ResponsiveFloraTypography fontWeight='bold'>
                  Baixar extrato completo
                </ResponsiveFloraTypography>
              </FloraButton>
            )}

            {order?.invoiceFileLink && (
              <FloraButton
                as='a'
                download={`Nota Fiscal - ${order.id}.pdf`}
                href={order.invoiceFileLink}
                target='_blank'
                rel='noopener noreferrer'
                hierarchy={config.app.short === 'bot' ? 'secondary' : 'primary'}
                autotagging-flora='on'
                data-flora='button'
                data-flora-text='baixar-nota-fiscal'
                icon={<TrayArrowDownIcon size='20' />}
                has='iconRight'
              >
                <ResponsiveFloraTypography fontWeight='bold'>
                  Baixar nota fiscal
                </ResponsiveFloraTypography>
              </FloraButton>
            )}
          </Flex>
        </Card>
      )}

      <Card>
        <ResponsiveFloraTypography as='h2' fontSize='subtitle' fontWeight='bold'>
          {order?.deliveryAddress.deliveryMode === 'takeaway'
            ? 'Endereço de retirada'
            : 'Endereço de entrega'}
        </ResponsiveFloraTypography>

        <Styles.Divider variant='Line' />
        <ResponsiveFloraTypography
          css={{
            textTransform: 'capitalize',
          }}
        >
          {order && order.deliveryAddress.fullAddress.toLowerCase()}
        </ResponsiveFloraTypography>
      </Card>
    </Styles.Wrapper>
  );
};
